/**
 * Auto-join functionality for new users
 * Automatically adds new users to default groups and events
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { addGroupMember } from './groups';
import { createRSVP } from './events';

/**
 * Get all groups marked for auto-join
 */
export const getDefaultGroups = async () => {
  try {
    const groupsRef = collection(db, 'groups');
    const q = query(groupsRef, where('autoJoin', '==', true));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('[autoJoin] Error fetching default groups:', error);
    return [];
  }
};

/**
 * Get all events marked for auto-join
 */
export const getDefaultEvents = async () => {
  try {
    const eventsRef = collection(db, 'events');
    const q = query(eventsRef, where('autoJoin', '==', true));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('[autoJoin] Error fetching default events:', error);
    return [];
  }
};

/**
 * Automatically join a new user to default groups and events
 * This is called after user account creation
 */
export const autoJoinDefaults = async (userId) => {
  try {
    // Get default groups and events
    const [defaultGroupIds, defaultEventIds] = await Promise.all([
      getDefaultGroups(),
      getDefaultEvents(),
    ]);

    // Join default groups
    const groupPromises = defaultGroupIds.map(async (groupId) => {
      try {
        await addGroupMember(groupId, {
          userId,
          role: 'member',
          joinedAt: new Date(),
        });
        console.log(`[autoJoin] ✅ Added user ${userId} to default group ${groupId}`);
      } catch (error) {
        console.error(`[autoJoin] ❌ Failed to add user to group ${groupId}:`, error);
        // Don't throw - continue with other groups
      }
    });

    // RSVP to default events
    const eventPromises = defaultEventIds.map(async (eventId) => {
      try {
        await createRSVP({
          userId,
          eventId,
          state: 'going',
          plusOnes: 0,
        });
        console.log(`[autoJoin] ✅ Added user ${userId} to default event ${eventId}`);
      } catch (error) {
        console.error(`[autoJoin] ❌ Failed to add user to event ${eventId}:`, error);
        // Don't throw - continue with other events
      }
    });

    // Wait for all joins to complete (but don't fail if some fail)
    await Promise.all([...groupPromises, ...eventPromises]);

    console.log(`[autoJoin] ✅ Completed auto-join for user ${userId}: ${defaultGroupIds.length} groups, ${defaultEventIds.length} events`);
  } catch (error) {
    console.error('[autoJoin] Error in autoJoinDefaults:', error);
    // Don't throw - signup should still succeed even if auto-join fails
  }
};
